import { CacheService, LogService } from '@absg/angular-common';
import { DateFormat, DateTimeHelper } from '@absg/angular-platform';
import {
  ConfigApi, EmrPracticeConfig, GetPatientMedOrdersRequest, MedOrder, MedOrderApi,
  MedOrderResponse, MedOrderTransaction, MedOrderTransactionRequest, PatientsWithMedOrders,
} from '@absg/api-emr-integration';
import { MedOrderTransactionHeader } from '@absg/api-inventory';
import { BaseService, CacheKeys } from '@absg/nim-client-shared';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class EmrService extends BaseService {
  futureMedOrdersRefreshTimestamp: Date;

  constructor(
    loggingService: LogService,
    private stationInfoService: StationInfoService,
    private medOrderApi: MedOrderApi,
    private configApi: ConfigApi,
    private cacheService: CacheService,
    private httpClient: HttpClient,
  ) {
    super(loggingService);
  }

  getPatientMedOrders(clinicId: number, demandOrderDate: Date, refreshData = false): Observable<PatientsWithMedOrders[]> {
    const currentClinic = this.stationInfoService.practiceClinicDetail.getValue();
    let startEndDemandOrderDate = demandOrderDate || this.createDate();
    startEndDemandOrderDate = DateTimeHelper.convertUTCToLocalJSDate(startEndDemandOrderDate);
    const request = {
      clinicId: clinicId || currentClinic?.clinicId,
      startDate: startEndDemandOrderDate,
      endDate: startEndDemandOrderDate,
      useCachedData: !!this.futureMedOrdersRefreshTimestamp && !refreshData,
    } as GetPatientMedOrdersRequest;

    return this.medOrderApi.getPatientMedOrders(request)
      .pipe(
        tap(() => {
          if (!request.useCachedData) {
            this.futureMedOrdersRefreshTimestamp = this.createDate();
          }
        }),
        map(response => response as PatientsWithMedOrders[]),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  createDate(): Date {
    return new Date(); // overwritten in unit tests to make the date consistent
  }

  getMedOrderByPatientNumber(patientNumber: string, clinicId?: number, medOrderDate?: Date): Observable<MedOrderResponse> {
    if (!clinicId) {
      const clinicDetail = this.stationInfoService.practiceClinicDetail.getValue();
      clinicId = clinicDetail.clinicId;
    }
    return this.medOrderApi.getMedOrdersResponseV3(patientNumber, clinicId, DateTimeHelper.formatDateTime(medOrderDate ?? new Date(), DateFormat.WorldDateOnly))
      .pipe(
        map(response => response as MedOrderResponse),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  getMedOrderByPatientPartyId(patientPartyId: number, clinicId?: number, medOrderDate?: Date): Observable<MedOrderResponse> {
    if (!clinicId) {
      const clinicDetail = this.stationInfoService.practiceClinicDetail.getValue();
      clinicId = clinicDetail.clinicId;
    }
    return this.medOrderApi.getMedOrdersByPatientPartyId(clinicId, DateTimeHelper.formatDateTime(medOrderDate ?? new Date(), DateFormat.WorldDateOnly), patientPartyId)
      .pipe(
        map(response => response as MedOrderResponse),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  createMedOrderByPatientNumber(patientNumber: string, medOrder: MedOrder): Observable<MedOrder> {
    const clinicDetail = this.stationInfoService.practiceClinicDetail.getValue();

    return this.medOrderApi.createMedOrders(patientNumber, clinicDetail.clinicId, medOrder)
      .pipe(
        map(response => response as MedOrder),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  getEmrConfigForClinicId(clinicId: number): Observable<EmrPracticeConfig> {
    return this.configApi.getConfig(clinicId)
      .pipe(
        tap(response => this.cacheService.set(CacheKeys.emrConfig, response)),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  getEmrConfig(isAdmin: boolean): Observable<EmrPracticeConfig> {
    const emrConfigKey = isAdmin ? CacheKeys.emrConfigAdmin : CacheKeys.emrConfig;
    const emrConfigDetail: BehaviorSubject<EmrPracticeConfig> = this.cacheService.get(emrConfigKey);
    if (!!(emrConfigDetail.getValue())) {
      return emrConfigDetail.asObservable();
    }
    return this.stationInfoService.practiceClinicDetail.pipe(
      filter((clinicInfo) => !!clinicInfo),
      concatMap((clinicDetail) => {
        const clinicId = clinicDetail.clinicId;
        const getConfig$ = isAdmin ? this.configApi.getConfigAdmin(clinicId) : this.configApi.getConfig(clinicId);
        return getConfig$
          .pipe(
            tap((response: EmrPracticeConfig) => this.cacheService.set(emrConfigKey, response)),
            map(response => response as EmrPracticeConfig),
            catchError(response => this.handleErrorResponse(response)),
          );
      }),
    );
  }

  createEmrConfig(emrPracticeConfig: EmrPracticeConfig): Observable<EmrPracticeConfig> {
    this.clearCachedEmrConfig();
    return this.configApi.createConfig(emrPracticeConfig)
      .pipe(
        map(response => response as EmrPracticeConfig),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  updateEmrConfig(emrPracticeConfig: EmrPracticeConfig): Observable<EmrPracticeConfig> {
    this.clearCachedEmrConfig();
    return this.configApi.updateConfig(emrPracticeConfig)
      .pipe(
        map(response => response as EmrPracticeConfig),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  getFutureMedOrdersClinics(): Observable<Array<number>> {
    const cachedFutureMedOrdersClinics = this.cacheService.get(CacheKeys.futureMedOrdersClinics);
    if (cachedFutureMedOrdersClinics.value) {
      return of(cachedFutureMedOrdersClinics.value);
    } else {
      return this.configApi.getFutureMedOrdersClinics()
        .pipe(
          catchError(error => {
            this.handleErrorMessage(error);
            return of([]);
          }),
          tap(results => {
            this.cacheService.set(CacheKeys.futureMedOrdersClinics, results);
          }),
        );
    }
  }

  clearCachedEmrConfig(): void {
    this.cacheService.set(CacheKeys.emrConfig, null);
    this.cacheService.set(CacheKeys.emrConfigAdmin, null);
    this.cacheService.set(CacheKeys.futureMedOrdersClinics, null);
  }

  getMedOrderTransactionsForEMROrder(emrOrderId: string): Observable<MedOrderTransaction[]> {
    if (emrOrderId.length === 0) {
      return of([]);
    }

    const request = {
      EmrOrderId: emrOrderId,
    } as MedOrderTransactionRequest;

    return this.medOrderApi.getAllTransactionsFromEMROrder(request)
      .pipe(
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  getMedOrderTransactionsForDevTool(emrOrderId: string): Observable<MedOrderTransactionHeader> {
    // CHEATING UNTIL WE UPGRADE TO ANGULAR 15
    const request = { emrOrderId } as MedOrderTransactionRequest;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path = (this.medOrderApi as any).getEndpoint() + '/medorders/transactions-dev-tool';

    return this.httpClient.post(path, request);
  }
}
